import { AsyncPipe, DOCUMENT, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { from, map, mergeMap, Observable } from 'rxjs';
import { AppShellNoRenderDirective } from 'src/app/common/app-shell-render/app-shell-no-render.directive';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { BottomBarComponent } from 'src/app/_shared/components/bottom-bar/bottom-bar.component';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { HeroComponent } from 'src/app/_shared/components/hero/hero.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { LocationSearchService } from 'src/app/common/location-search/location-search.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { PeopleAvatarListComponent } from 'src/app/_shared/components/people-avatar-list/people-avatar-list.component';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { SignupBoxComponent } from 'src/app/_shared/components/signup-box/signup-box.component';
import { TypedTemplateDirective } from 'src/app/common/typed-template/typed-template.directive';
import { UserNamePipe } from 'src/app/common/user-name/user-name.pipe';
import { UserService } from 'src/app/common/user/user.service';
import { getDefaultModalSettings, sharrylandId } from 'src/app/common/utils/helpers';
import { environment } from 'src/environments/environment';
import { Hashtag, Post, User } from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { GetUserFollowersGQL, GetUserFollowingsGQL } from '../profile/profile.query.generated';
import {
  CommunityFollowersGQL,
  CommunityFollowingsGQL,
  CommunityHashtagsRankingGQL,
  CommunityRecommendedUsersGQL,
  CommunitySharrylandPostsGQL,
} from './community.query.generated';
import { Capacitor } from '@capacitor/core';
import { RouteTranslatePipe } from '../../_shared/pipes/route-translate.pipe';
import { SlugPipe } from '../../_shared/pipes/slug.pipe';
import { ContentTypeColorizerPipe } from '../../_shared/pipes/content-type-colorizer.pipe';

@Component({
  selector: 'sharry-community',
  standalone: true,
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    AsyncPipe,
    NgTemplateOutlet,
    I18NextModule,
    MatButtonModule,
    RouterLink,
    RouterOutlet,
    SlugPipe,
    RouteTranslatePipe,
    AvatarDirective,
    EllipsisTooltipDirective,
    UserNamePipe,
    ContentTypeColorizerPipe,
    IconComponent,
    SignupBoxComponent,
    TypedTemplateDirective,
    PeopleAvatarListComponent,
    AppShellNoRenderDirective,
    BottomBarComponent,
    HeaderComponent,
    FooterComponent,
    HeroComponent,
  ],
})
export class CommunityComponent extends BaseComponent implements OnInit, OnDestroy {
  hastags$: Observable<Partial<Hashtag[]>>;
  followers$: Observable<Partial<{ followers: User[]; followersCount: number }>>;
  followings$: Observable<Partial<{ followings: User[]; followingsCount: number }>>;
  sharrylandPosts$: Observable<Partial<Post[]>>;
  recommendedProfiles$: Observable<Partial<User[]>>;
  showHero = false;
  random = Math.random();
  typeToken: { $implicit: User };
  followerBusy = false;
  followingBusy = false;
  public env = environment.env;
  heroImages = [
    {
      ['480']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/community/1-480.webp`,
      ['768']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/community/1-768.webp`,
      ['1024']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/community/1-1024.webp`,
      ['1440']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/community/1-1440.webp`,
      ['1920']: `https://media.sharry.land/app/images/hero/${
        this.env === 'production' ? 'production' : 'staging'
      }/community/1-1920.webp`,
      title: 'community.title',
      subtitle: 'community.subtitle',
      textColor: '#fff',
    },
  ];

  constructor(
    private locationSearchService: LocationSearchService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    public userService: UserService,
    private communityFollowingsGQL: CommunityFollowingsGQL,
    private communityFollowersGQL: CommunityFollowersGQL,
    private communitySharrylandPostsGQL: CommunitySharrylandPostsGQL,
    private communityRecommendedUsersGQL: CommunityRecommendedUsersGQL,
    private communityHashtagsRankingGQL: CommunityHashtagsRankingGQL,
    private cd: ChangeDetectorRef,
    private openDialogService: OpenDialogService,
    private getUserFollowingsGQL: GetUserFollowingsGQL,
    private getUserFollowersGQL: GetUserFollowersGQL,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
  }

  ngOnInit(): void {
    if (!Capacitor.isNativePlatform()) {
      (this.document.querySelector('.app-wrapper') as HTMLElement).setAttribute(
        'style',
        'overflow-x: visible !important',
      );
    }
    this.subscriptions.push(
      this.userService.user$.subscribe(user => {
        if (user) {
          this.followers$ = this.communityFollowersGQL.fetch({}).pipe(map(r => r.data.me as any));
          this.followings$ = this.communityFollowingsGQL.fetch({}).pipe(
            map(r => ({
              followings: r.data.me.followings.map(f => f.following) as User[],
              followingsCount: r.data.me.followingsCount,
            })),
          );
        }
      }),
    );
    this.sharrylandPosts$ = this.communitySharrylandPostsGQL
      .fetch({ id: sharrylandId })
      .pipe(map(r => r.data.allPosts as Post[]));
    this.hastags$ = this.communityHashtagsRankingGQL.fetch({ limit: 5 }).pipe(map(r => r.data.hashtagSearch));

    this.recommendedProfiles$ = this.communityRecommendedUsersGQL
      .fetch({}, { fetchPolicy: 'no-cache' })
      .pipe(map(r => r.data.recommendedUsers as User[]));
  }

  onUserSearch() {
    this.locationSearchService.onSearch([], [], true);
  }

  onShare() {
    const data = {
      data: {
        title: this.translateService.t('community.share-title'),
        content: this.userService.user,
      },
    };
    this.subscriptions.push(
      from(import('src/app/_shared/dialogs/social-sharing-dialog/social-sharing-dialog.component')).subscribe({
        next: ({ SocialSharingDialogComponent }) => {
          this.openDialogService.openDialog(SocialSharingDialogComponent, {
            ...getDefaultModalSettings(),
            height: 'auto',
            ...data,
          });
        },
      }),
    );
  }

  onActivate(event: any) {
    this.showHero = event.id !== 'single-post';
    this.cd.detectChanges();
  }

  onFollowersClick() {
    this.followerBusy = true;
    this.cd.detectChanges();
    this.subscriptions.push(
      this.getUserFollowersGQL.fetch({ slug: this.userService.user.slug }, { fetchPolicy: 'no-cache' }).subscribe({
        next: res => {
          if (res.data.userBySlug.followers) {
            this.followerBusy = false;
            this.cd.detectChanges();
            this.subscriptions.push(
              from(import('src/app/_shared/dialogs/people-dialog/people-dialog.component'))
                .pipe(
                  mergeMap(({ PeopleDialogComponent }) =>
                    this.openDialogService
                      .openDialog(PeopleDialogComponent, {
                        ...getDefaultModalSettings(),
                        data: {
                          people: res.data.userBySlug.followers.map(f => ({ user: { ...f } })),
                          headerText: this.translateService.t('community.who-follows'),
                        },
                      })
                      .afterClosed(),
                  ),
                )
                .subscribe({
                  next: user => {
                    if (user) {
                      this.router.navigate([this.routeTranslateService.translateRoute('profile'), user.slug]);
                    }
                  },
                }),
            );
          }
        },
      }),
    );
  }

  onFollowingsClick() {
    this.followingBusy = true;
    this.cd.detectChanges();
    this.subscriptions.push(
      this.getUserFollowingsGQL.fetch({ slug: this.userService.user.slug }, { fetchPolicy: 'no-cache' }).subscribe({
        next: res => {
          if (res.data.userBySlug.followings) {
            this.followingBusy = false;
            this.cd.detectChanges();
            this.subscriptions.push(
              from(import('src/app/_shared/dialogs/people-dialog/people-dialog.component'))
                .pipe(
                  mergeMap(({ PeopleDialogComponent }) =>
                    this.openDialogService
                      .openDialog(PeopleDialogComponent, {
                        ...getDefaultModalSettings(),
                        data: {
                          people: res.data.userBySlug.followings.map(f => ({ user: { ...f.following } })),
                          headerText: this.translateService.t('community.who-i-follow'),
                        },
                      })
                      .afterClosed(),
                  ),
                )
                .subscribe({
                  next: user => {
                    if (user) {
                      this.router.navigate([this.routeTranslateService.translateRoute('profile'), user.slug]);
                    }
                  },
                }),
            );
          }
        },
      }),
    );
  }

  ngOnDestroy() {
    if (!Capacitor.isNativePlatform()) {
      (this.document.querySelector('.app-wrapper') as HTMLElement).removeAttribute('style');
    }
  }
}
